/**
 * ApparatusPicker — Grouped visual picker for unit/apparatus selection.
 * Units are organized by type category (Engines, Ambulances & Medics,
 * Specialty, Command & Safety) in a two-column grid with fieldset-style
 * outlined boxes.
 * Flex units (F/BF suffix) are hidden from the picker.
 * Command & Safety units are only visible to officers and admins.
 * Fetches from API when available, falls back to shared defaults.
 */

import { useState, useEffect } from 'react';
import { APPARATUS, COMMAND_APPARATUS, isFlexUnit, getStationFromId } from '@shared/apparatus';
import { apiFetch } from '../lib/api';

interface ApparatusPickerProps {
  selected: string | null;
  onChange: (apparatus: string) => void;
  /** When true, shows the Command & Safety section for chief officer units */
  showCommandUnits?: boolean;
}

interface ApparatusOption {
  id: string;
  type: string;
  label: string;
  station: string;
}

/** Category definitions for type-based grouping */
const TYPE_CATEGORIES: { key: string; label: string; types: string[] }[] = [
  { key: 'engines', label: 'Engines', types: ['engine'] },
  { key: 'ambulances', label: 'Ambulances & Medics', types: ['ambulance', 'medic'] },
  { key: 'specialty', label: 'Specialty', types: ['tower', 'rescue', 'brush', 'utility'] },
];

/** Specialty types that go on the first row (Tower, Rescue, Brush) vs second row (Utility) */
const SPECIALTY_TOP_TYPES = new Set(['tower', 'rescue', 'brush']);

/** Command unit type IDs */
const COMMAND_TYPES = new Set(['buggy', 'safety', 'chief', 'assistant_chief']);

function isCommandType(type: string): boolean {
  return COMMAND_TYPES.has(type);
}

/** Sort units by station number for consistent display */
function sortByStation(items: ApparatusOption[]): ApparatusOption[] {
  return [...items].sort((a, b) => {
    const stationA = a.station === 'command' ? '00' : a.station;
    const stationB = b.station === 'command' ? '00' : b.station;
    return stationA.localeCompare(stationB);
  });
}

function UnitButton({
  app,
  isSelected,
  onChange,
  variant = 'default',
}: {
  app: ApparatusOption;
  isSelected: boolean;
  onChange: (id: string) => void;
  variant?: 'default' | 'command';
}) {
  const commandStyles = isSelected
    ? 'bg-amber-500/15 border-amber-500 text-amber-400 ring-1 ring-amber-500/30'
    : 'bg-surface-raised border-edge-default text-content-secondary hover:border-amber-500/50 hover:text-amber-400';

  const defaultStyles = isSelected
    ? 'bg-action-primary/15 border-action-primary text-action-primary ring-1 ring-action-primary/30'
    : 'bg-surface-raised border-edge-default text-content-secondary hover:border-edge-strong hover:text-content-primary';

  return (
    <button
      key={app.id}
      type="button"
      onClick={() => onChange(app.id)}
      className={`
        px-3 py-2 rounded-lg text-sm font-medium
        transition-all duration-150 cursor-pointer border
        ${variant === 'command' ? commandStyles : defaultStyles}
      `}
    >
      {app.id}
    </button>
  );
}

function CategoryBox({
  label,
  children,
  variant = 'default',
}: {
  label: string;
  children: React.ReactNode;
  variant?: 'default' | 'command';
}) {
  const borderColor = variant === 'command' ? 'border-amber-500/40' : 'border-edge-default';
  const labelColor = variant === 'command' ? 'text-amber-500' : 'text-content-tertiary';

  return (
    <fieldset className={`border ${borderColor} rounded-lg p-3 pt-2`}>
      <legend className={`px-2 text-[11px] font-semibold ${labelColor} uppercase tracking-wider`}>{label}</legend>
      {children}
    </fieldset>
  );
}

export default function ApparatusPicker({ selected, onChange, showCommandUnits = false }: ApparatusPickerProps) {
  const [options, setOptions] = useState<ApparatusOption[]>(
    APPARATUS.filter((a) => !isFlexUnit(a.id)).map((a) => ({
      id: a.id,
      type: a.type,
      label: a.label,
      station: a.station,
    })),
  );

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ data: { name: string; type: string; active: number }[] }>('/api/admin/apparatus')
      .then((res) => {
        if (!cancelled && res.data.length > 0) {
          const validNamePattern = /^[A-Z]{1,3}\d{3,4}(?:B?F)?$/;
          const apiItems = res.data
            .filter((a) => a.active && !isFlexUnit(a.name) && !isCommandType(a.type) && validNamePattern.test(a.name))
            .map((a) => ({
              id: a.name,
              type: a.type,
              label: a.name,
              station: getStationFromId(a.name),
            }));
          if (apiItems.length > 0) {
            const apiStations = new Set(apiItems.map((a) => a.station));
            const staticFallbacks = APPARATUS.filter((a) => !isFlexUnit(a.id) && !apiStations.has(a.station)).map(
              (a) => ({ id: a.id, type: a.type, label: a.label, station: a.station }),
            );
            setOptions([...apiItems, ...staticFallbacks]);
          }
        }
      })
      .catch(() => {
        /* Fall back to static defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Group options by type
  const byType: Record<string, ApparatusOption[]> = {};
  for (const opt of options) {
    if (!byType[opt.type]) byType[opt.type] = [];
    byType[opt.type].push(opt);
  }

  // Command apparatus — static defaults (chief units don't come from the API yet)
  const commandOptions: ApparatusOption[] = COMMAND_APPARATUS.map((a) => ({
    id: a.id,
    type: a.type,
    label: a.label,
    station: a.station,
  }));

  return (
    <div className="w-full">
      <label className="block text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">
        Unit / Apparatus
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TYPE_CATEGORIES.map((cat) => {
          const items = cat.types.flatMap((t) => byType[t] ?? []);
          if (items.length === 0) return null;
          const sorted = sortByStation(items);

          if (cat.key === 'specialty') {
            // Split specialty into two rows: TW/R/B on top, U on bottom
            const topRow = sorted.filter((a) => SPECIALTY_TOP_TYPES.has(a.type));
            const bottomRow = sorted.filter((a) => !SPECIALTY_TOP_TYPES.has(a.type));
            return (
              <CategoryBox key={cat.key} label={cat.label}>
                <div className="flex flex-wrap gap-1.5">
                  {topRow.map((app) => (
                    <UnitButton key={app.id} app={app} isSelected={selected === app.id} onChange={onChange} />
                  ))}
                </div>
                {bottomRow.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {bottomRow.map((app) => (
                      <UnitButton key={app.id} app={app} isSelected={selected === app.id} onChange={onChange} />
                    ))}
                  </div>
                )}
              </CategoryBox>
            );
          }

          if (cat.key === 'ambulances') {
            // Split ambulances & medics into two rows
            const ambulanceRow = sorted.filter((a) => a.type === 'ambulance');
            const medicRow = sorted.filter((a) => a.type === 'medic');
            return (
              <CategoryBox key={cat.key} label={cat.label}>
                <div className="flex flex-wrap gap-1.5">
                  {ambulanceRow.map((app) => (
                    <UnitButton key={app.id} app={app} isSelected={selected === app.id} onChange={onChange} />
                  ))}
                </div>
                {medicRow.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {medicRow.map((app) => (
                      <UnitButton key={app.id} app={app} isSelected={selected === app.id} onChange={onChange} />
                    ))}
                  </div>
                )}
              </CategoryBox>
            );
          }

          return (
            <CategoryBox key={cat.key} label={cat.label}>
              <div className="flex flex-wrap gap-1.5">
                {sorted.map((app) => (
                  <UnitButton key={app.id} app={app} isSelected={selected === app.id} onChange={onChange} />
                ))}
              </div>
            </CategoryBox>
          );
        })}

        {/* Command & Safety — officer/admin only */}
        {showCommandUnits && (
          <CategoryBox label="Command & Safety" variant="command">
            {/* BC + SAF on top */}
            <div className="flex flex-wrap gap-1.5">
              {sortByStation(commandOptions.filter((a) => a.type === 'buggy' || a.type === 'safety')).map((app) => (
                <UnitButton
                  key={app.id}
                  app={app}
                  isSelected={selected === app.id}
                  onChange={onChange}
                  variant="command"
                />
              ))}
            </div>
            {/* Chiefs row */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {sortByStation(commandOptions.filter((a) => a.type === 'chief')).map((app) => (
                <UnitButton
                  key={app.id}
                  app={app}
                  isSelected={selected === app.id}
                  onChange={onChange}
                  variant="command"
                />
              ))}
            </div>
            {/* Assistant Chiefs row */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {sortByStation(commandOptions.filter((a) => a.type === 'assistant_chief')).map((app) => (
                <UnitButton
                  key={app.id}
                  app={app}
                  isSelected={selected === app.id}
                  onChange={onChange}
                  variant="command"
                />
              ))}
            </div>
          </CategoryBox>
        )}
      </div>
    </div>
  );
}
